/**
 * Sector display helpers — keeps the screener readable when sector names
 * are long, while preserving the full name for tooltips, accessibility,
 * and the deep-linked Sectors page.
 */

// Short labels used inside the Screener Sector column (≤ 12 chars).
// Anything not listed falls back to the full sector name.
const ABBREV = {
  "Telecommunication Services": "Telecoms",
  "Food Beverage & Tobacco": "Food & Bev",
  "Diversified Financials": "Div Finance",
  "Real Estate & REITs": "Real Estate",
  "Capital Goods": "Cap Goods",
  "Consumer Services": "Consumer",
  "Diversified Holdings": "Div Holdings",
  "Hotels & Travels": "Hotels",
  "Hotels & Restaurants": "Hotels",
  "Banks": "Banks",
  "Insurance": "Insurance",
  "Plantations": "Plantations",
  "Automobiles & Components": "Autos",
  "Materials": "Materials",
  "Energy": "Energy",
  "Utilities": "Utilities",
  "Health Care Equipment & Services": "Healthcare",
  "Pharmaceuticals, Biotechnology & Life Sciences": "Pharma",
  "Software & Services": "Software",
  "Technology Hardware & Equipment": "Tech HW",
  "Retailing": "Retailing",
  "Transportation": "Transport",
  "Commercial & Professional Services": "Pro Services",
  "Household & Personal Products": "Household",
  "Consumer Durables & Apparel": "Apparel",
  "Media & Entertainment": "Media",
  "Semiconductors & Semi Equipment": "Semis",
  "Construction & Engineering": "Construction",
};

/**
 * Returns a short label suitable for narrow screener columns. Falls back
 * to the original name when the abbreviation isn't defined and the name
 * is already short (<= 12 chars).
 */
export const abbreviateSector = (sector) => {
  if (!sector) return "—";
  if (ABBREV[sector]) return ABBREV[sector];
  if (sector.length <= 12) return sector;
  // Best-effort: drop common stop-words then trim
  const trimmed = sector
    .replace(/\bServices\b/i, "Svcs")
    .replace(/\bDiversified\b/i, "Div")
    .replace(/\b(?:and|And)\b/g, "&");
  return trimmed.length <= 14 ? trimmed : trimmed.slice(0, 12) + "…";
};

/**
 * Tailwind classes for the OVER / NEUT / UNDER verdict pill.
 * Phase 11 polish — solid filled badge with white bold text so the
 * verdict reads instantly at a glance. Identical colours to
 * `SectorVerdictPill` (single source of truth).
 */
export const verdictPillClasses = (verdict) => {
  const v = (verdict || "").toUpperCase();
  if (v === "OVERWEIGHT") {
    // #15803d
    return "bg-[hsl(142_72%_29%)] text-white border-transparent";
  }
  if (v === "UNDERWEIGHT") {
    // #dc2626
    return "bg-[hsl(0_72%_45%)] text-white border-transparent";
  }
  if (v === "NEUTRAL") {
    // #1d4ed8 — blue, more distinctive than grey
    return "bg-[hsl(221_83%_47%)] text-white border-transparent";
  }
  return "bg-muted text-muted-foreground border-border";
};

export const verdictShort = (verdict) => {
  const v = (verdict || "").toUpperCase();
  if (v === "OVERWEIGHT") return "OVER";
  if (v === "UNDERWEIGHT") return "UNDER";
  if (v === "NEUTRAL") return "NEUT";
  return null;
};
